import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowLeft, Plus, BookOpen, Pencil } from "lucide-react";
import { TspCard, TspTable, TspBadge, TspBreadcrumb, TspEmptyState } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import useTspApiResource, { tspErrorMessage } from "../../hooks/useTspApiResource";
import notification from "../../../../helpers/notification";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import { formatDate, formatNumber } from "../../utils/tspFormat";
import { DISTRIBUTION_METHOD_LABEL } from "../../constants/tspOperations";
import BookDistributionEditModal from "./components/BookDistributionEditModal";

export default function TspBookDistributionPage() {
  // ?journey= — the journey the person came from (set after submitting a
  // distribution); Back returns to its details page.
  const journeyId = new URLSearchParams(useLocation().search).get("journey") || "";
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.BOOK_DISTRIBUTION_MANAGE);
  const list = useTspApiResource(() => TspApiService.listBookDistributions({ limit: 50 }), []);
  const summary = useTspApiResource(() => TspApiService.getBookDistributionSummary(), []);
  const [editing, setEditing] = useState(null); // the record being edited

  const handleUpdate = async (payload) => {
    try {
      await TspApiService.updateBookDistribution(editing.id, payload);
      notification("success", "Book distribution updated.");
      setEditing(null);
      list.reload();
      summary.reload(); // the total may have changed
      return { ok: true };
    } catch (error) {
      const message = tspErrorMessage(error, "The entry could not be updated.");
      notification("error", message);
      return { ok: false, message };
    }
  };

  const columns = [
    { key: "date", label: "Date", render: (row) => formatDate(row.created_at.slice(0, 10)) },
    { key: "journey", label: "Journey", render: (row) => row.journey.journey_name },
    { key: "city", label: "City", render: (row) => row.city_visit.location?.label || "—" },
    { key: "bookType", label: "Book Type", render: (row) => row.book_name },
    { key: "qty", label: "Qty", align: "right", render: (row) => row.quantity },
    { key: "method", label: "Method", render: (row) => DISTRIBUTION_METHOD_LABEL[row.distribution_method] },
  ];
  // Entries can be edited only while their journey is in progress (the server's rule).
  if (canManage) {
    columns.push({
      key: "actions",
      label: "Actions",
      align: "right",
      render: (row) =>
        row.journey.status === "IN_PROGRESS" ? (
          <button type="button" className="tsp-btn tsp-btn-outline-primary tsp-btn-sm" onClick={() => setEditing(row)} aria-label={`Edit ${row.book_name} entry`}>
            <Pencil size={12} aria-hidden="true" /> Edit
          </button>
        ) : (
          <span className="tsp-muted-text" title="The journey is no longer in progress">
            —
          </span>
        ),
    });
  }

  const renderBody = () => {
    if (list.error) return <TspLoadError error={list.error} onRetry={list.reload} what="book distribution records" />;
    if (!list.data) return <div className="tsp-dashboard-loading">Loading…</div>;
    if (list.data.length === 0) {
      return <TspEmptyState icon={BookOpen} title="No book distribution recorded yet" message="Once a journey is in progress, distribution entries recorded there will appear here." />;
    }
    return <TspTable columns={columns} rows={list.data} rowKey="id" />;
  };

  return (
    <div className="tsp-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>Book Distribution</h1>
          <TspBreadcrumb items={["Dashboard", "Book Distribution"]} />
        </div>
        <div className="tsp-bd-heading-actions">
          <Link to={journeyId ? `/tsp/journeys/${journeyId}` : "/tsp/journeys"} className="tsp-btn tsp-btn-outline-primary">
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </Link>
          {canManage && (
            <Link to={`/tsp/book-distribution/new${journeyId ? `?journey=${journeyId}` : ""}`} className="tsp-btn tsp-btn-success">
              <Plus size={15} /> Add Distribution
            </Link>
          )}
        </div>
      </div>

      <TspCard
        title="All Distribution Records"
        action={summary.data && <TspBadge tone="amber">{formatNumber(summary.data.total_quantity)} Total Books</TspBadge>}
      >
        {renderBody()}
      </TspCard>

      {editing && <BookDistributionEditModal record={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}

      <ToastContainer />
    </div>
  );
}
